import { useCampaignsStore } from '@/stores/campaigns'
import { useUserStore } from '@/stores/user'
import type { Campaign, User } from '@/types'
import { EntryFilterType, Icons } from '@/utils/helper-objects'
import { extractDetailsFromReferralUrl } from '@/utils/utils'
import { type SiteSlug, ThemeType, getTheme } from '@goodlifeplus/themes'
import type { RouteRecordProps } from 'env'
import type {
  NavigationGuardNext,
  RouteLocation,
  RouteRecordRaw,
} from 'vue-router'

import AuthMiddleware from './middleware/auth'

export default [
  {
    path: '/member-zone',
    beforeEnter: [AuthMiddleware],
    children: [
      {
        path: '',
        name: 'members-zone',
        meta: {
          title: "Member's Zone",
          nav: {
            sidebar: {
              display: () => {
                return useUserStore().isLoggedIn
              },
              text: 'Dashboard',
              icon: Icons.Entries,
              category: 'members-zone',
              order: 0,
            },
          },
        },
        component: () =>
          import('../views/MembersZone/MembersZoneDashboardLayoutProvider.vue'),
        props() {
          return {
            user: useUserStore().user,
          }
        },
      },
      {
        path: 'refer',
        name: 'members-zone-refer',
        props(route: RouteRecordProps<{ user: User; campaign: Campaign }>) {
          return {
            user: route.meta.props.user,
            campaign: route.meta.props.campaign,
          }
        },
        meta: {
          title: 'Refer A Friend',
          nav: {
            sidebar: {
              display: () =>
                useUserStore().isLoggedIn &&
                getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type !==
                  ThemeType.Subscription,
              text: 'Referral',
              icon: Icons.Gift,
              category: 'members-zone',
              order: 2,
            },
          },
        },
        component: () => import('../views/MembersZone/MemberReferView.vue'),
        async beforeEnter(
          to: RouteLocation,
          _from: RouteLocation,
          next: NavigationGuardNext
        ) {
          if (
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription
          ) {
            return next({ name: 'not-found' })
          }

          const campaignStore = useCampaignsStore()
          const user = useUserStore().user!
          const { referralId } = extractDetailsFromReferralUrl(
            user.referral_link
          )

          if (!referralId) {
            return next({ name: 'not-found' })
          }

          try {
            const campaign =
              await campaignStore.fetchCampaignByCampaignId(referralId)

            to.meta.props = {
              ...(to.meta.props ?? {}),
              user,
              campaign,
            }

            next()
          } catch {
            return next({ name: 'not-found' })
          }
        },
      },
      {
        path: 'details',
        name: 'members-zone-details',
        props() {
          return {
            user: useUserStore().user,
          }
        },
        meta: {
          title: 'My Profile',
        },
        component: () => import('../views/MembersZone/MemberDetailsView.vue'),
      },
      {
        path: 'loyalty',
        name: 'members-zone-loyalty',
        props() {
          return {
            user: useUserStore().user,
          }
        },
        meta: {
          title: 'My Loyalty',
        },
        component: () => import('../views/MembersZone/MemberLoyaltyView.vue'),
      },
      {
        path: 'transactions',
        name: 'members-zone-balance-history',
        meta: {
          title: 'My Transactions',
        },
        component: () =>
          import('../views/MembersZone/MemberTransactionsView.vue'),
      },
      {
        path: 'points-history',
        name: 'members-zone-points-history',
        meta: {
          title: 'Points History',
        },
        component: () =>
          import('../views/MembersZone/MemberRewardTransactionsView.vue'),
      },
      {
        path: 'withdrawals',
        name: 'members-zone-balance-withdrawal',
        meta: {
          title: 'Withdrawals',
        },
        component: () =>
          import('../views/MembersZone/MemberWithdrawalsView.vue'),
      },
      {
        path: 'claim',
        name: 'members-zone-prize-claim',
        meta: {
          title: 'My Prizes',
        },
        component: () =>
          import('../views/MembersZone/MemberPrizeFulfilmentView.vue'),
      },
      {
        path: 'entries',
        children: [
          {
            path: '',
            name: 'members-zone-entries',
            props(route) {
              return {
                entryFilter:
                  route.hash.slice(1) === EntryFilterType.InstantGames
                    ? EntryFilterType.InstantGames
                    : EntryFilterType.Subscriptions,
              }
            },
            meta: {
              title: 'My Entries',
            },
            component: () =>
              import('../views/MembersZone/MemberEntriesView.vue'),
          },
          {
            path: ':code',
            name: 'members-zone-entries-detail',
            meta: {
              title: 'My Entries',
            },
            props: true,
            component: () =>
              import('../views/MembersZone/MemberEntriesDetailView.vue'),
          },
        ],
      },
      {
        path: 'rewards',
        name: 'members-zone-rewards',
        component: () => import('../views/MembersZone/MemberRewardsView.vue'),
        meta: {
          title: () =>
            getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
            ThemeType.Subscription
              ? 'Rewards'
              : 'Points & Rewards',
          nav: {
            sidebar: {
              display: () =>
                getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
                ThemeType.Subscription
                  ? useUserStore().isLoggedIn
                  : true,
              icon: () =>
                getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
                ThemeType.Subscription
                  ? Icons.Reward
                  : Icons.Ticket,
              text: () =>
                getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
                ThemeType.Subscription
                  ? 'Rewards'
                  : 'Points & Rewards',
              category: () =>
                getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug).type ===
                ThemeType.Subscription
                  ? 'default'
                  : 'members-zone',
              order: 1,
            },
          },
        },
      },
      {
        path: 'player-limits',
        name: 'members-zone-player-limits',
        meta: {
          title: 'Player Limits',
        },
        component: () =>
          import('../views/MembersZone/MemberPlayerLimitsView.vue'),
      },
      {
        path: 'benefits',
        beforeEnter(_to, _from, next) {
          return next({ name: 'members-zone-rewards', replace: true })
        },
      },
    ],
  },
] as Readonly<RouteRecordRaw[]>
